<template>
	<div class="modal-column">
		<!-- 科目只读展示：批量新增基于某个具体科目，这里只是回显，不让用户改 -->
		<div class="p-8px flex items-center">
			<span class="mr-8px">科目：</span>
			<Input v-model="subjectName" readonly style="width:240px"/>
		</div>

		<!-- 功能说明：本弹窗用于设置「多辅助核算」的组合方式 -->
		<div class="px-8px">设置辅助核算组合方式：</div>

		<!-- =====【批量新增功能·改动C】每个核算类别加「全选/取消全选」按钮（随当前 tab 切换类别）开始 ===== -->
		<!-- 左侧是类别切换 Tabs；右侧是对「当前类别」的全选 / 取消全选按钮 -->
		<div v-if="tabDatas.length" class="px-8px flex items-center justify-between">
			<Tabs :datas="tabDatas" v-model="selected"/>
			<Button size="s" :color="allCheckedCurrent ? '' : 'primary'"
			        :disabled="!currentDetails.length" @click="toggleSelectAll">
				{{ allCheckedCurrent ? '取消全选' : '全选本类' }}
			</Button>
		</div>
		<!-- =====【批量新增功能·改动C】... 结束 ===== -->

		<!-- 勾选明细表格区域（flex 占满剩余高度） -->
		<div class="px-8px flex-1 h-0px pt-8px">
				<!-- =====【批量新增功能·新增文件·改动A】勾选明细表格：checkField 把勾选状态写回 row.checked，切 tab 勾选不丢 开始 ===== -->
				<!--
				  checkbox-config.checkField='checked' 让 vxe-table 把复选框状态直接写回行对象的 checked 字段。
				  由于每个类别各自维护一份行数据（detailMap），切换 tab（即切换 currentDetails）时，
				  每个类别各自的勾选状态天然保留，不会因表格数据被替换而丢失。
				-->
				<vxe-table ref="xTable" border height="auto"
								 :data="currentDetails"
								 :checkbox-config="{checkField: 'checked'}"
								 @checkbox-change="refreshTip"
								 @checkbox-all="refreshTip">
				<!-- =====【批量新增功能·新增文件·改动A】勾选明细表格 结束 ===== -->
				<vxe-column type="checkbox" width="50" align="center"/>
				<vxe-column title="编码" field="code" min-width="120"/>
				<vxe-column title="名称" field="name" min-width="180"/>
			</vxe-table>
		</div>

		<!-- 实时提示：各分类已选数量 + 组合后将生成的明细总条数 -->
		<div class="px-8px py-6px tip-text">{{ tipText }}</div>

		<!-- 底部操作按钮：取消 / 确定 -->
		<div class="modal-column-between">
			<Button @click="$emit('close')" icon="fa fa-close">取消</Button>
			<Button @click="onConfirm" icon="fa fa-check" color="primary">确定</Button>
		</div>
	</div>
</template>

<script>
/**
 * 【新增文件·批量新增功能】期初辅助核算明细 —— 批量新增
 * ------------------------------------------------------------------
 * 功能定位：
 *   单辅助核算：勾选子项即可；多辅助核算：按类别分别勾选后做组合（笛卡尔积）生成明细行。
 * 交互入口：
 *   由父组件 InitialAuxiliary.vue 的「批量新增」按钮通过 layer.open 调用。
 * 数据流：
 *   父组件传入 subject（科目）/ categories（辅助核算类别及其明细列表）；
 *   本组件让用户按类别勾选，最后把组合结果以 rows 数组 emit('confirm') 回父组件。
 * 说明：本文件为本次新增，无旧代码可对比。
 */
export default {
	name: "InitialAuxiliaryBatch",
}
</script>

<script setup>
import {defineProps, defineEmits, ref, reactive, computed} from "vue";
import {message} from "heyui.ext";

// ===== 1. 入参与事件定义 =====
const props = defineProps(['subject', 'categories']);   // subject: 当前科目；categories: 辅助核算类别（含 details 明细）
const emits = defineEmits(['close', 'confirm']);          // close: 关闭弹窗；confirm: 提交组合结果

// 顶部只读展示的科目名称（优先 subjectName，其次全称 subjectFullName）
const subjectName = ref(props.subject.subjectName || props.subject.subjectFullName || '');

// 缓存类别列表，避免后续重复取 props
const categories = props.categories || [];

// ===== 2. 类别 Tab 与行数据初始化 =====
// 每个辅助核算类别对应一个 tab（key 统一转字符串，便于和 detailMap 的键对齐）
const tabDatas = categories.map(c => ({title: c.name, key: String(c.id)}));
// 默认选中第一个类别的 tab
const selected = ref(tabDatas.length ? tabDatas[0].key : null);

// 每个类别维护一份独立的可勾选行数据。
// 关键点：checked 字段由 vxe-table 的 checkField 回写，切 tab 时勾选状态不会丢。
const detailMap = reactive({});
categories.forEach(c => {
	detailMap[String(c.id)] = (c.details || []).map(d => ({
		id: d.id,        // 明细 id
		code: d.code,    // 明细编码
		name: d.name,    // 明细名称
		checked: false  // 是否勾选（初始未选）
	}));
});

// 表格实例引用，部分场景下需要手动 loadData 刷新勾选态
const xTable = ref(null);

// 当前 tab 对应的明细列表（computed，自动随 selected 变化）
const currentDetails = computed(() => detailMap[selected.value] || []);

// ===== 3. 已勾选分组（按类别聚合）=====
// 每个类别一份「已勾选明细」数组，用于组合逻辑与提示文案
const selectedGroups = computed(() => categories.map(c => (detailMap[String(c.id)] || []).filter(d => d.checked)));

// =====【批量新增功能·改动C】当前类别是否已全部勾选（控制按钮文案/颜色）开始 =====
const allCheckedCurrent = computed(() => {
	const list = detailMap[selected.value] || [];
	return list.length > 0 && list.every(d => d.checked);
});
// =====【批量新增功能·改动C】... 结束 =====

// =====【批量新增功能·改动C】全选/取消全选当前类别（随当前 tab 切换）开始 =====
const toggleSelectAll = () => {
	const list = detailMap[selected.value] || [];
	if (!list.length) {
		return;
	}
	// toggle 语义：当前类别只要有一个没勾，就全勾上；否则就全部取消
	const willCheck = !list.every(d => d.checked);
	list.forEach(d => {
		d.checked = willCheck;
	});
	// 通知 vxe-table 重新按 checkField 渲染勾选态
	if (xTable.value && typeof xTable.value.loadData === 'function') {
		xTable.value.loadData(currentDetails.value);
	}
};
// =====【批量新增功能·改动C】... 结束 =====

// ===== 4. 实时提示文案 =====
const tipText = computed(() => {
	// 每个类别已选数量，如「客户已选 2 项，部门已选 3 项」
	const parts = categories.map((c, i) => `${c.name}已选 ${selectedGroups.value[i].length} 项`);
	// 组合总条数 = 各分类已选数量之积（笛卡尔积）
	const total = selectedGroups.value.reduce((acc, group) => acc * group.length, 1);
	return `${parts.join('，')}；确定后将组合生成 ${total} 条明细`;
});

// 勾选状态已由 checkField 写回行数据，computed 会自动更新，这里仅作为表格事件的回调占位
const refreshTip = () => {
};

// ===== 5. 确认：组合生成明细行（笛卡尔积）=====
const onConfirm = () => {
	const groups = selectedGroups.value;
	// 任一类别未勾选则阻断，给出明确提示
	const emptyIndex = groups.findIndex(group => !group.length);
	if (emptyIndex > -1) {
		message(`请先选择「${categories[emptyIndex].name}」的明细项`);
		return;
	}
	// =====================【批量新增功能·新增文件·改动B：组合核心逻辑 开始】=====================
	// 思路：从空对象种子 [{}] 出发，逐个类别叠加。
	// 行结构：{ 类别id: 明细id }，例如 { '1': 11, '2': 21 } 表示「客户=11 且 部门=21」。
	let rows = [{}];
	categories.forEach((c, i) => {
		const field = String(c.id);
		const next = [];
		rows.forEach(base => {
			groups[i].forEach(detail => {
				// 把当前明细拼到已有组合之后，形成一条新组合
				next.push(Object.assign({}, base, {[field]: detail.id}));
			});
		});
		rows = next;
	});
	// 把组合结果交回父组件（InitialAuxiliary.mergeRows 负责并入表格 + 去重）
	emits('confirm', rows);
	// =====================【批量新增功能·新增文件·改动B：组合核心逻辑 结束】=====================
}
</script>

<style scoped lang="less">
.tip-text {
	font-size: 12px;
	color: #909399;
}
</style>
